import { useEffect, useRef, type RefObject } from 'react'
import { ArrowUpDown, ChevronDown, SlidersHorizontal, X } from 'lucide-react'
import { Button } from '../../ui/Button'
import { activeFilterCount, clearFilter, filterChips, pluralHosts, sortChipText, type CatalogQuery } from './catalogQuery'

/**
 * Search, the Filter-and-sort control, the result count, and a chip for
 * everything applied.
 *
 * The chips are why the panel can start closed: what is filtered and how the
 * list is sorted are on screen without opening anything. The sort chip cannot
 * be removed — there is always a sort — so it opens the panel at the Sort
 * group instead.
 */
export function CatalogToolbar({ query, onChange, onClearFilters, resultCount, open, onToggle, onEditSort, toggleRef, countRef }: {
  query: CatalogQuery
  onChange: (next: CatalogQuery) => void
  onClearFilters: () => void
  resultCount: number
  open: boolean
  onToggle: () => void
  onEditSort: () => void
  toggleRef: RefObject<HTMLButtonElement>
  countRef: RefObject<HTMLSpanElement>
}) {
  const count = activeFilterCount(query)
  const chips = filterChips(query)
  const chipRefs = useRef<Array<HTMLButtonElement | null>>([])
  // The index whose remove button should take focus after a chip unmounts.
  const pendingFocus = useRef<number | null>(null)

  useEffect(() => {
    const index = pendingFocus.current
    if (index === null) return
    pendingFocus.current = null
    // The chip that slid into the removed one's place is the "next" one; past
    // the end, the last one left; with none left, the control that adds them.
    const next = chipRefs.current[Math.min(index, chips.length - 1)]
    if (chips.length > 0 && next) next.focus()
    else toggleRef.current?.focus()
  })

  const remove = (index: number, key: (typeof chips)[number]['key']) => {
    pendingFocus.current = index
    onChange(clearFilter(query, key))
  }

  return <>
    <div className="catalog-toolbar">
      <input
        type="search"
        className="catalog-search"
        aria-label="Search hosts"
        placeholder="Search hosts by name, region or capability"
        value={query.search}
        onChange={(event) => onChange({ ...query, search: event.target.value })}
      />
      <Button
        ref={toggleRef}
        type="button"
        variant="secondary"
        className={`catalog-filter-toggle${count > 0 ? ' catalog-filter-toggle--active' : ''}`}
        aria-expanded={open}
        aria-controls="catalog-filters"
        onClick={onToggle}
      >
        <SlidersHorizontal size={16} aria-hidden="true" />
        <span>Filter and sort</span>
        {count > 0 && <span className="catalog-filter-count" aria-label={`${count} active filter${count === 1 ? '' : 's'}`}>{count}</span>}
        <ChevronDown size={16} aria-hidden="true" className={open ? 'catalog-filter-caret-open' : ''} />
      </Button>
      <span ref={countRef} className="result-count" role="status" aria-live="polite" tabIndex={-1}>{pluralHosts(resultCount)}</span>
    </div>
    <div className="catalog-chips">
      <button type="button" className="catalog-chip catalog-chip--sort" aria-label={`Sorted by ${sortChipText(query)}. Change sort`} onClick={onEditSort}>
        <ArrowUpDown size={14} aria-hidden="true" />
        <span className="catalog-chip-text">{sortChipText(query)}</span>
      </button>
      {chips.map((chip, index) => <span key={chip.key} className="catalog-chip">
        <span className="catalog-chip-text">{chip.text}</span>
        <button type="button" ref={(node) => { chipRefs.current[index] = node }} className="catalog-chip-remove" aria-label={`Remove ${chip.text}`} onClick={() => remove(index, chip.key)}>
          <X size={12} aria-hidden="true" />
        </button>
      </span>)}
      {chips.length > 0 && <button type="button" className="catalog-clear-filters" onClick={() => { pendingFocus.current = 0; onClearFilters() }}>Clear filters</button>}
    </div>
  </>
}
